/**
 * Working Solution Dilution Calculator
 * Utility for calculating dilution ratios from stock to working solutions
 */

import { round } from './chemicalCalculations';

interface DilutionParams {
  stockEC: number;           // mS/cm
  targetEC: number;          // mS/cm
  waterEC?: number;          // mS/cm
  stockSolutionVolume: number; // Liters
  containerVolume: number;   // Liters
}

interface DilutionResult {
  stockSolutionAmount: number; // mL
  waterAmount: number;        // L
  finalEC: number;            // mS/cm
  dilutionRatio: string;      // X:Y string representation
}

/**
 * Calculate dilution from stock solution to working solution
 * @param params Dilution parameters
 */
export function calculateDilution(params: DilutionParams): DilutionResult {
  const { stockEC, targetEC, waterEC = 0, stockSolutionVolume, containerVolume } = params;
  
  // Calculate the required amount of stock solution in mL
  // Formula: ((target EC - water EC) * container volume) / (stock EC - water EC)
  const targetECDiff = targetEC - waterEC;
  const stockECDiff = stockEC - waterEC;
  
  // If the stock EC isn't greater than water EC, we can't dilute properly
  if (stockECDiff <= 0) {
    throw new Error('Stock solution EC must be greater than water EC');
  }
  
  // Calculate stock solution amount in liters, then convert to mL
  const stockSolutionAmountL = (targetECDiff * containerVolume) / stockECDiff;
  const stockSolutionAmount = stockSolutionAmountL * 1000; // Convert to mL
  
  // Calculate water amount (container volume - stock solution volume)
  const waterAmount = containerVolume - stockSolutionAmountL;
  
  // Calculate final EC after dilution
  const finalEC = waterEC + (stockSolutionAmountL * stockECDiff / containerVolume);
  
  // Calculate dilution ratio (simplified fraction)
  const gcd = (a: number, b: number): number => b ? gcd(b, a % b) : a;
  
  // Use 1000 for mL precision and round to nearest integer for ratio calculation
  const stockPart = Math.round(stockSolutionAmount);
  const waterPart = Math.round(waterAmount * 1000);
  const divisor = gcd(stockPart, waterPart);
  
  const simplifiedStockPart = stockPart / divisor;
  const simplifiedWaterPart = waterPart / divisor;
  
  const dilutionRatio = `${simplifiedStockPart}:${simplifiedWaterPart}`;
  
  return {
    stockSolutionAmount: round(stockSolutionAmount, 1),
    waterAmount: round(waterAmount, 3),
    finalEC: round(finalEC, 2),
    dilutionRatio
  };
}

/**
 * Calculate stock solution amount needed to achieve target working solution
 * @param targetEC Target EC for working solution (mS/cm)
 * @param stockEC Stock solution EC (mS/cm)
 * @param containerVolume Working solution container volume (L)
 * @param waterEC Base water EC (mS/cm)
 * @returns Amount of stock solution in mL
 */
export function calculateStockAmount(
  targetEC: number,
  stockEC: number,
  containerVolume: number,
  waterEC: number = 0
): number {
  // Ensure valid inputs
  if (stockEC <= waterEC) {
    throw new Error('Stock solution EC must be greater than water EC');
  }
  
  if (targetEC <= waterEC) {
    throw new Error('Target EC must be greater than water EC');
  }
  
  if (targetEC >= stockEC) {
    throw new Error('Target EC must be less than stock solution EC');
  }
  
  // Calculate required stock solution in mL
  // Formula: ((target EC - water EC) * container volume * 1000) / (stock EC - water EC)
  const stockAmount = ((targetEC - waterEC) * containerVolume * 1000) / (stockEC - waterEC);
  
  return round(stockAmount, 0);
}

export default {
  calculateDilution,
  calculateStockAmount
};
